/**
 * TimelineCell Component
 *
 * Renders a date range (start to end) with visual representation.
 * Clicking opens a dual date picker for range selection.
 */

import { useState, useRef, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import type { TimelineCellProps } from './types';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function TimelineCell({
  value,
  onChange,
  readonly = false,
}: TimelineCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value?.start) {
      return new Date(value.start);
    }
    return new Date();
  });
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [tempStart, setTempStart] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectingEnd(false);
        setTempStart(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const calculateDuration = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleDateSelect = (day: number) => {
    if (readonly || !onChange) return;

    const selectedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const dateStr = selectedDate.toISOString().split('T')[0] as string;

    if (!selectingEnd) {
      // First click: set start date
      setTempStart(dateStr);
      setSelectingEnd(true);
    } else {
      // Second click: set end date
      if (tempStart) {
        const start = new Date(tempStart);
        const end = selectedDate;

        // Ensure start <= end
        if (start <= end) {
          onChange({
            start: tempStart,
            end: dateStr,
          });
        } else {
          onChange({
            start: dateStr,
            end: tempStart,
          });
        }
      }
      setIsOpen(false);
      setSelectingEnd(false);
      setTempStart(null);
    }
  };

  const handleClear = () => {
    if (readonly || !onChange) return;
    onChange(null);
    setIsOpen(false);
    setSelectingEnd(false);
    setTempStart(null);
  };

  const navigateMonth = (delta: number) => {
    setViewDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days: {
      day: number;
      date: string;
      isCurrentMonth: boolean;
      isToday: boolean;
      isStart: boolean;
      isEnd: boolean;
      isInRange: boolean;
    }[] = [];

    const today = new Date();
    const startDate = tempStart || value?.start;
    const endDate = value?.end;

    // Helper to check if date is in range
    const isInRange = (dateStr: string) => {
      if (!startDate || !endDate) return false;
      const date = new Date(dateStr);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return date > start && date < end;
    };

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = new Date(year, month - 1, day);
      const dateStr = date.toISOString().split('T')[0] as string;
      days.push({
        day,
        date: dateStr,
        isCurrentMonth: false,
        isToday: false,
        isStart: dateStr === startDate,
        isEnd: dateStr === endDate,
        isInRange: isInRange(dateStr),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0] as string;
      const isToday = today.getDate() === day &&
        today.getMonth() === month &&
        today.getFullYear() === year;

      days.push({
        day,
        date: dateStr,
        isCurrentMonth: true,
        isToday,
        isStart: dateStr === startDate,
        isEnd: dateStr === endDate,
        isInRange: isInRange(dateStr),
      });
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      const dateStr = date.toISOString().split('T')[0] as string;
      days.push({
        day,
        date: dateStr,
        isCurrentMonth: false,
        isToday: false,
        isStart: dateStr === startDate,
        isEnd: dateStr === endDate,
        isInRange: isInRange(dateStr),
      });
    }

    return days;
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {value?.start && value?.end ? (
          <div className="flex items-center gap-1.5 text-sm text-gray-700">
            <span>{formatDate(value.start)}</span>
            <ArrowRightIcon className="h-3 w-3 text-gray-400" />
            <span>{formatDate(value.end)}</span>
            <span className="text-xs text-gray-500 ml-1">
              ({calculateDuration(value.start, value.end)}d)
            </span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        onClick={() => setIsOpen(true)}
        className="w-full h-full flex items-center cursor-pointer min-h-[28px] text-left"
      >
        {value?.start && value?.end ? (
          <div className="flex items-center gap-1.5 text-sm text-gray-700">
            <span>{formatDate(value.start)}</span>
            <ArrowRightIcon className="h-3 w-3 text-gray-400" />
            <span>{formatDate(value.end)}</span>
            <span className="text-xs text-gray-500 ml-1">
              ({calculateDuration(value.start, value.end)}d)
            </span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
        )}
      </button>

      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-dropdown border border-surface-border p-3 w-72">
          {/* Header with month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-surface-hover rounded"
            >
              <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-900">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-surface-hover rounded"
            >
              <ChevronRightIcon className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {/* Selection hint */}
          <div className="text-xs text-gray-500 text-center mb-2">
            {selectingEnd ? 'Select end date' : 'Select start date'}
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {generateCalendarDays().map((day, index) => (
              <button
                key={index}
                onClick={() => day.isCurrentMonth && handleDateSelect(day.day)}
                disabled={!day.isCurrentMonth}
                className={`
                  p-2 text-sm transition-colors relative
                  ${day.isCurrentMonth ? 'hover:bg-primary-50' : 'text-gray-300 cursor-default'}
                  ${day.isToday ? 'font-bold text-primary-600' : ''}
                  ${day.isStart || day.isEnd ? 'bg-primary-500 text-white hover:bg-primary-600 rounded-md z-10' : ''}
                  ${day.isInRange ? 'bg-primary-100' : ''}
                `}
              >
                {day.day}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="mt-3 pt-3 border-t border-surface-border flex justify-between">
            <button
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                setSelectingEnd(false);
                setTempStart(null);
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Done
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
}
